export type ExportFormat = "csv" | "tsv";

export type ExportMode =
  | "performance"
  | "index"
  | "scatter"
  | "exposure"
  | "rolling"
  | "statistics"
  | "correlation";

export type ExportOptions = {
  mode?: ExportMode;
  precision?: number; // decimal places, overrides mode default if provided
  format?: ExportFormat; // default: "csv"
};

// Default precision by mode
const MODE_PRECISION: Record<ExportMode, number> = {
  performance: 4,
  index: 2,
  scatter: 6,
  exposure: 2,
  rolling: 4,
  statistics: 4,
  correlation: 4,
};

const escapeValue = (value: unknown, format: ExportFormat): string => {
  if (value === null || value === undefined) return "";

  let stringValue: string;
  if (typeof value === "string") {
    stringValue = value;
  } else if (value instanceof Date) {
    stringValue = value.toISOString();
  } else if (typeof value === "object") {
    stringValue = JSON.stringify(value);
  } else {
    stringValue = String(value);
  }

  if (format === "csv") {
    // CSV: escape double quotes and wrap in quotes if contains comma, quote, or newline
    const escaped = stringValue.replace(/"/g, '""');
    return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
  } else {
    // TSV: escape tabs and newlines by replacing with spaces
    return stringValue.replace(/[\t\n\r]/g, " ");
  }
};

const formatDateYYYYMMDD = (input: unknown): string => {
  if (!input) return "";
  const d = input instanceof Date ? input : new Date(input as string | number);
  if (isNaN(d.getTime())) return String(input);
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
    .toISOString()
    .slice(0, 10);
};

export const downloadDelimited = (
  data: Record<string, unknown>[],
  cols: { id: string; label: string }[],
  filename: string = "data",
  options?: ExportOptions
) => {
  if (!Array.isArray(data) || data.length === 0) {
    console.error("Data is empty.");
    return;
  }
  if (!Array.isArray(cols) || cols.length === 0) {
    console.error("Columns are empty.");
    return;
  }

  const format = options?.format ?? "csv";
  const delimiter = format === "csv" ? "," : "\t";
  const extension = format === "csv" ? ".csv" : ".tsv";
  const mimeType =
    format === "csv"
      ? "text/csv;charset=utf-8;"
      : "text/tab-separated-values;charset=utf-8;";

  // Determine precision based on mode or explicit override
  const mode = options?.mode;
  const precision =
    options?.precision ?? (mode ? MODE_PRECISION[mode] : undefined);

  const headers = cols
    .map((col) => escapeValue(col.label, format))
    .join(delimiter);

  const content = data
    .map((row) =>
      cols
        .map((col) => {
          const raw = row[col.id];

          // Handle date column
          if (col.id === "date") {
            return escapeValue(formatDateYYYYMMDD(raw), format);
          }

          // Convert string numbers to actual numbers
          let value = raw;
          if (
            typeof value === "string" &&
            value.trim() !== "" &&
            !isNaN(Number(value))
          ) {
            value = Number(value);
          }

          // Apply precision formatting for numbers if mode/precision specified
          if (typeof value === "number" && precision !== undefined) {
            return escapeValue(value.toFixed(precision), format);
          }

          return escapeValue(value, format);
        })
        .join(delimiter)
    )
    .join("\n");

  const output = [headers, content].join("\n");

  // Ensure filename has correct extension
  const finalFilename = filename.endsWith(extension)
    ? filename
    : filename.replace(/\.(csv|tsv)$/, "") + extension;

  const blob = new Blob([output], { type: mimeType });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", finalFilename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the object URL to prevent memory leaks
  URL.revokeObjectURL(url);
};
